import { Button } from '@/components/ui/Button';
import { ThinkingCube } from './ThinkingCube';
import { useState, useEffect, useRef } from 'react';
import { Check, Loader2, Brain, ShieldAlert, Sparkles, RefreshCw, Square, Trash2 } from 'lucide-react';
import { labApi } from '@/lib/api';
import { onSocketReady, unsubscribeFromLaboratorium, getSocket } from '@/lib/socket';
import { useConfirmDialog } from '@/contexts/ConfirmDialogContext';
import { getKnowledgePipelineFailure, getStaleContentPipelineFailure } from './knowledgeGenerationState';
import { LabConfiguratorShell } from './LabConfiguratorShell';
import type { ChoiceCard } from './types';
import {
  automaticGenerationChoice,
  centeredChecklistScrollTop,
  completedProductImageNames,
  firstIncompletePipelineStep,
  getProductImagesRunFailure,
  getProductImagesRunState,
  isProgressEventForRun,
  isTextGenerationComplete,
  selectGenerationKnowledgeBase,
} from './generationRunState';

interface PipelineItem {
  type: string;
  name: string;
  title?: string;
  icon?: string;
  progressMessages?: string[];
  imageCount?: number;
}

interface ChoiceSectionState {
  original?: string;
  awaitingChoice?: boolean;
  choiceOptions?: string[];
  choiceCards?: ChoiceCard[];
}

/** Strip markdown formatting from AI-generated choice options */
function cleanChoiceText(text: string): string {
  return text
    .replace(/\*\*/g, '')  // bold
    .replace(/\*/g, '')    // italic
    .replace(/^[""]|[""]$/g, '') // quotes at start/end
    .replace(/^#+\s*/g, '') // headers
    .trim();
}

const EVIDENCE_STATUS_LABELS: Record<ChoiceCard['evidenceStatus'], string> = {
  fact: 'Fakt z danych',
  hypothesis: 'Hipoteza konwersyjna',
  claim_to_verify: 'Claim do potwierdzenia',
  needs_validation: 'Do walidacji',
  missing: 'Brak statusu',
};

interface GenerationPanelProps {
  productId: string;
  /** Pipeline type: 'KNOWLEDGE' for KB, or content type like 'ADVERTORIAL', 'LANDING', etc. */
  mode?: string;
  /** Override pipeline resolution (e.g. video strategy pipeline for VIDEO_SCRIPT) */
  pipelineOverride?: string;
  /** Content ID — required when mode is not KNOWLEDGE (for choice submission & polling) */
  contentId?: string | null;
  knowledgeBaseId?: string | null;
  kbName: string;
  isFirstGeneration: boolean;
  userInputs?: Record<string, string>;
  restartKnowledge?: boolean;
  resuming?: boolean;
  resumeAlreadyStarted?: boolean;
  backLabel?: string;
  onBack?: () => void;
  onRestart?: () => Promise<void>;
  onComplete: () => void;
  onError: (msg: string) => void;
}

// Product-profile & customer-insight focused messages (no repeats — linear sequence)
const KB_MESSAGES = [
  'Analizuję profil idealnego klienta...',
  'Identyfikuję główne bóle i frustracje grupy docelowej...',
  'Wyodrębniam unikalne cechy produktu (USP)...',
  'Opracowuję unikalny mechanizm działania...',
  'Budowuję matrycę korzyści produktu...',
  'Analizuję ścieżkę decyzyjną klienta...',
  'Mapuję obiekcje i wątpliwości kupujących...',
  'Definiuję ton komunikacji marki...',
  'Tworzę profil psychograficzny klienta...',
  'Analizuję przewagi nad konkurencją...',
  'Badam motywacje zakupowe...',
  'Przygotowuję argumenty sprzedażowe...',
  'Opracowuję propozycję wartości...',
  'Budowuję bazę social proof...',
  'Finalizuję profil produktu...',
];

// Content generation messages
const CONTENT_MESSAGES = [
  'Analizuję bazę wiedzy produktu...',
  'Przygotowuję strukturę treści...',
  'Tworzę angażujący wstęp...',
  'Buduję narrację sprzedażową...',
  'Dobieriam ton i styl komunikacji...',
  'Opracowuję elementy social proof...',
  'Tworzę sekcje z korzyściami...',
  'Formułuję wezwania do działania...',
  'Optymalizuję pod konwersję...',
  'Dopracowuję szczegóły...',
  'Łączę wszystkie sekcje w całość...',
  'Finalizuję treść...',
];

const IMAGE_MESSAGES = [
  'Analizuję referencje produktu...',
  'Przygotowuję finalny prompt obrazowy...',
  'Dopasowuję kartę stylu do kompozycji...',
  'Tworzę grafikę produktową...',
  'Zapisuję obraz na Dysku Google...',
  'Przechodzę do kolejnej grafiki...',
];

const CONTENT_TYPE_LABELS: Record<string, string> = {
  ADVERTORIAL: 'advertorial',
  LANDING: 'landing page',
  PRODUCT_IMAGES: 'zdjęcia produktowe',
  LISTICALE: 'listicale',
  QUIZ: 'quiz',
  VIDEO_SCRIPT: 'skrypt wideo',
  EXPLAINER: 'skrypt explainer',
  VSL: 'skrypt VSL',
  VSL_STORY: 'skrypt VSL storytelling',
  HOOKS: 'hooki reklamowe',
  AD_COPY: 'ad copy',
  AD_GRAPHIC: 'reklamę statyczną',
  AD_CREATIVE: 'kreację reklamową',
  UGC_SCRIPT: 'skrypt UGC',
  SOCIAL_POST: 'post social media',
};

function pipelineForGenerationMode(mode: string): string {
  return mode === 'AD_GRAPHIC' ? 'AD_CREATIVE' : mode;
}

function generationRequestError(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'response' in error) {
    return (error as { response?: { data?: { error?: string } } }).response?.data?.error || fallback;
  }
  return error instanceof Error ? error.message : fallback;
}

function isCancelledGenerationMessage(message: string): boolean {
  const normalized = message.toLocaleLowerCase('pl-PL');
  return normalized.includes('zatrzym') || normalized.includes('anulow') || normalized.includes('cancelled');
}

const COMPLETE_REDIRECT_SECONDS = 5;

export function GenerationPanel({ productId, mode = 'KNOWLEDGE', pipelineOverride, contentId, knowledgeBaseId, kbName, isFirstGeneration: _isFirstGeneration, userInputs, restartKnowledge, resuming, resumeAlreadyStarted, backLabel = 'Zamknij', onBack, onRestart, onComplete, onError }: GenerationPanelProps) {
  const confirm = useConfirmDialog();
  const isKB = mode === 'KNOWLEDGE';
  const isProductImages = mode === 'PRODUCT_IMAGES';
  const pipelineName = pipelineOverride || pipelineForGenerationMode(mode);
  const messages = isKB ? KB_MESSAGES : isProductImages ? IMAGE_MESSAGES : CONTENT_MESSAGES;
  const contentTypeLabel = CONTENT_TYPE_LABELS[mode] || mode.toLowerCase();

  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [currentField, setCurrentField] = useState<string | null>(null);
  const [pipelineFields, setPipelineFields] = useState<PipelineItem[]>([]);
  const [completedFields, setCompletedFields] = useState<Set<string>>(new Set());
  const [messageIdx, setMessageIdx] = useState(0);
  const [showComplete, setShowComplete] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [cancelled, setCancelled] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [resumingCancelled, setResumingCancelled] = useState(false);
  const [restartingCancelled, setRestartingCancelled] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(COMPLETE_REDIRECT_SECONDS);
  const startedRef = useRef(false);
  const resumeRequestStartedRef = useRef(false);
  const pipelineFieldsRef = useRef<PipelineItem[]>([]);
  const repairFieldNamesRef = useRef<Set<string> | null>(null);
  const completeTriggeredRef = useRef(false);
  const errorTriggeredRef = useRef(false);
  const activeKbIdRef = useRef<string | null>(knowledgeBaseId ?? null);
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);
  const checklistScrollRef = useRef<HTMLDivElement | null>(null);
  const choiceScrollRef = useRef<HTMLDivElement | null>(null);
  const checklistItemRefs = useRef<Record<string, HTMLDivElement | null>>({});
  // Track contentId that may arrive after generation starts
  const contentIdRef = useRef<string | null | undefined>(contentId);
  useEffect(() => { contentIdRef.current = contentId; }, [contentId]);
  useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  const triggerError = (message: string) => {
    if (errorTriggeredRef.current) return;
    errorTriggeredRef.current = true;
    if (isProductImages) {
      setGenerationError(message);
      return;
    }
    onErrorRef.current(message);
  };

  // Choice field state
  const [choiceField, setChoiceField] = useState<string | null>(null);
  const [choiceOptions, setChoiceOptions] = useState<string[]>([]);
  const [choiceCards, setChoiceCards] = useState<ChoiceCard[]>([]);
  const [selectedChoice, setSelectedChoice] = useState<string | null>(null);
  const [customChoice, setCustomChoice] = useState('');
  const [submittingChoice, setSubmittingChoice] = useState(false);
  const [automaticChoices, setAutomaticChoices] = useState(false);
  const autoSubmittedField = useRef<string | null>(null);
  const [regeneratingChoices, setRegeneratingChoices] = useState(false);

  useEffect(() => {
    choiceScrollRef.current?.scrollTo({ top: 0 });
  }, [choiceField, choiceOptions.length, choiceOptions[0]]);

  useEffect(() => {
    if (choiceField || showComplete || !currentField) return;
    const frame = window.requestAnimationFrame(() => {
      const container = checklistScrollRef.current;
      const activeItem = checklistItemRefs.current[currentField];
      if (!container || !activeItem) return;
      const containerRect = container.getBoundingClientRect();
      const itemRect = activeItem.getBoundingClientRect();
      const nextTop = centeredChecklistScrollTop({
        currentScrollTop: container.scrollTop,
        containerTop: containerRect.top,
        containerHeight: container.clientHeight,
        itemTop: itemRect.top,
        itemHeight: itemRect.height,
        scrollHeight: container.scrollHeight,
      });
      container.scrollTo({ top: nextTop, behavior: 'smooth' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [choiceField, currentField, pipelineFields.length, showComplete]);

  const markPipelineStarted = () => {
    const firstField = pipelineFieldsRef.current[0];
    if (!firstField) return;

    setCurrentField(prev => prev || firstField.name);
    setProgress(prev => ({
      current: Math.max(prev.current, 1),
      total: prev.total || pipelineFieldsRef.current.length,
    }));
  };

  useEffect(() => {
    setMessageIdx(0);
  }, [currentField, choiceField]);

  // Advance through configured progress messages
  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIdx(prev => prev + 1);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  // Load pipeline fields — include field + choice types, and expand image variants.
  useEffect(() => {
    labApi.getPipeline(pipelineName).then(res => {
      const allItems = res.data.data?.items || [];
      const items = allItems.flatMap((item: PipelineItem) => {
        if (item.type === 'image_gen') {
          const count = Math.max(1, Math.min(12, Number(item.imageCount || 1)));
          return Array.from({ length: count }, (_, index) => ({
            ...item,
            name: count > 1 ? `${item.name}_${index + 1}` : item.name,
            title: count > 1 ? `${item.title || item.name} ${index + 1}/${count}` : item.title,
          }));
        }
        return item.type === 'field' || item.type === 'choice' ? [item] : [];
      });
      setPipelineFields(items);
      pipelineFieldsRef.current = items;
      if (items.length === 0) {
        triggerError('Pipeline nie ma zdefiniowanych kroków. Skontaktuj się z administratorem.');
        return;
      }
      // Set total immediately from pipeline so progress bar shows X/total from the start
      if (items.length > 0) {
        setProgress(prev => ({ ...prev, total: items.length }));
        if (startedRef.current && !completeTriggeredRef.current) markPipelineStarted();
      }
    }).catch(err => {
      console.error('Failed to load pipeline config:', err);
      triggerError('Nie udało się załadować konfiguracji pipeline’u. Spróbuj ponownie.');
    });
  }, [pipelineName]);

  // Start generation (skip if resuming — pipeline already running on backend)
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    if (resuming) return; // Pipeline already running, just listen for progress

    if (isKB) {
      labApi.generateKnowledge(productId, { name: kbName, userInputs, restart: restartKnowledge })
        .then(res => {
          const targetId = res.data?.targetId || res.data?.data?.targetId;
          if (typeof targetId === 'string') activeKbIdRef.current = targetId;
          markPipelineStarted();
        })
        .catch(err => {
          console.error('Failed to start KB generation:', err);
          const message = err?.response?.data?.error || 'Nie udało się rozpocząć generowania bazy wiedzy.';
          triggerError(message);
        });
    }
    // Content generation is started externally (handleGenerateContent) — we just listen for progress
  }, [productId, kbName, isKB, restartKnowledge]);

  // Resume stale/in-flight KB generation safely. Backend ignores duplicates for an active run.
  useEffect(() => {
    if (!resuming || !isKB || resumeAlreadyStarted || resumeRequestStartedRef.current) return;
    if (pipelineFieldsRef.current.length === 0) return;
    resumeRequestStartedRef.current = true;

    let cancelled = false;
    const resumeKnowledgePipeline = async () => {
      try {
        const res = await labApi.getKnowledgeBases(productId);
        if (cancelled) return;
        const kbs = res.data.data || res.data;
        if (!Array.isArray(kbs)) return;
        const kb = selectGenerationKnowledgeBase(kbs, activeKbIdRef.current);
        if (!kb?.id || kb.isComplete) return;
        activeKbIdRef.current = kb.id;

        const persistedError = getKnowledgePipelineFailure(kb);
        if (persistedError) {
          triggerError(persistedError);
          return;
        }

        const sections = (kb.sections || {}) as Record<string, ChoiceSectionState>;
        if (sections.__pipeline_state?.original === 'cancelled') {
          setCancelled(true);
          return;
        }
        const fields = pipelineFieldsRef.current;
        const fieldNameSet = new Set(fields.map(field => field.name));
        const completedSectionNames = Object.keys(sections).filter(
          key => fieldNameSet.has(key) && sections[key]?.original && !sections[key]?.awaitingChoice,
        );

        if (completedSectionNames.length > 0) {
          setCompletedFields(prev => {
            const next = new Set(prev);
            for (const name of completedSectionNames) next.add(name);
            return next;
          });
          setProgress(prev => ({ current: Math.max(prev.current, completedSectionNames.length), total: prev.total || fields.length }));
        }

        const awaitingField = fields.find(field => sections[field.name]?.awaitingChoice && sections[field.name]?.choiceOptions?.length);
        if (awaitingField) {
          setChoiceField(awaitingField.name);
          setChoiceOptions(sections[awaitingField.name]?.choiceOptions || []);
          setChoiceCards(sections[awaitingField.name]?.choiceCards || []);
          setSelectedChoice(null);
          setCustomChoice('');
          setRegeneratingChoices(false);
          return;
        }

        const firstIncomplete = fields.find(field => !sections[field.name]?.original);
        if (!firstIncomplete) return;
        setCurrentField(firstIncomplete.name);
        await labApi.generateKnowledge(productId, {
          kbId: kb.id,
          name: kb.name || kbName,
          resumeFrom: firstIncomplete.name,
        });
      } catch (err) {
        console.error('Failed to resume KB generation:', err);
        triggerError('Nie udało się wznowić generowania bazy wiedzy.');
      }
    };

    resumeKnowledgePipeline();
    return () => {
      cancelled = true;
    };
  }, [resuming, resumeAlreadyStarted, isKB, productId, kbName, pipelineFields.length]);

  useEffect(() => {
    if (!resuming || isKB || isProductImages || !contentIdRef.current || resumeRequestStartedRef.current) return;
    if (pipelineFieldsRef.current.length === 0) return;
    resumeRequestStartedRef.current = true;
    let disposed = false;
    const resumeContentPipeline = async () => {
      try {
        const targetId = contentIdRef.current;
        if (!targetId) return;
        const response = await labApi.getContent(productId, targetId);
        if (disposed) return;
        const content = response.data.data || response.data;
        const sections = (content.sections || {}) as Record<string, ChoiceSectionState>;
        if (sections.__pipeline_state?.original === 'cancelled') {
          setCancelled(true);
          return;
        }

        let fields = pipelineFieldsRef.current;
        if (isTextGenerationComplete(content)) {
          const missingResponse = await labApi.getContentMissingFields(productId, targetId);
          if (disposed) return;
          const missingFields = missingResponse.data.data.missingFields;
          if (missingFields.length === 0) {
            triggerComplete();
            return;
          }

          const configuredByName = new Map(fields.map(field => [field.name, field]));
          fields = missingFields.map(field => configuredByName.get(field.name) || {
            type: 'field',
            name: field.name,
            title: field.title,
          });
          repairFieldNamesRef.current = new Set(fields.map(field => field.name));
          pipelineFieldsRef.current = fields;
          setPipelineFields(fields);
        }
        const completedSectionNames = fields
          .filter(field => sections[field.name]?.original && !sections[field.name]?.awaitingChoice)
          .map(field => field.name);
        setCompletedFields(new Set(completedSectionNames));
        setProgress({ current: completedSectionNames.length, total: fields.length });

        const awaitingField = fields.find(field => sections[field.name]?.awaitingChoice && sections[field.name]?.choiceOptions?.length);
        if (awaitingField) {
          setChoiceField(awaitingField.name);
          setChoiceOptions(sections[awaitingField.name]?.choiceOptions || []);
          setChoiceCards(sections[awaitingField.name]?.choiceCards || []);
          return;
        }

        const firstIncomplete = fields.find(field => !sections[field.name]?.original);
        if (firstIncomplete) setCurrentField(firstIncomplete.name);
        await labApi.resumeContent(productId, targetId, { resumeFrom: firstIncomplete?.name });
        if (!disposed) markPipelineStarted();
      } catch (err) {
        if (disposed) return;
        if (err && typeof err === 'object' && 'response' in err
          && (err as { response?: { status?: number } }).response?.status === 409) {
          triggerComplete();
          return;
        }
        triggerError(generationRequestError(err, 'Nie udało się wznowić generowania treści.'));
      }
    };
    void resumeContentPipeline();
    return () => { disposed = true; };
  }, [resuming, isKB, isProductImages, productId, pipelineFields.length]);

  const resolveTargetId = async (): Promise<string | null> => {
    if (!isKB) return contentIdRef.current || null;
    if (activeKbIdRef.current) return activeKbIdRef.current;
    const response = await labApi.getKnowledgeBases(productId);
    const knowledgeBases = response.data.data || response.data;
    const active = Array.isArray(knowledgeBases)
      ? knowledgeBases.find((item: { isComplete?: boolean }) => !item.isComplete)
      : null;
    if (active?.id) activeKbIdRef.current = active.id;
    return active?.id || null;
  };

  const handleCancelGeneration = async () => {
    const accepted = await confirm({
      title: 'Przerwać generowanie?',
      message: 'Aktualny request AI zostanie zatrzymany. Ukończone checkpointy pozostaną zapisane i będzie można wznowić proces od pierwszego brakującego kroku.',
      confirmLabel: 'Przerwij generowanie',
      cancelLabel: 'Kontynuuj generowanie',
      danger: true,
    });
    if (!accepted) return;
    setCancelling(true);
    try {
      const targetId = await resolveTargetId();
      if (!targetId) throw new Error('Nie udało się ustalić aktywnego celu generowania.');
      await labApi.cancelPipeline(productId, { pipeline: pipelineName, targetId });
      setCancelled(true);
    } catch (err) {
      triggerError(err instanceof Error ? err.message : 'Nie udało się przerwać generowania.');
    } finally {
      setCancelling(false);
    }
  };

  const handleResumeCancelled = async () => {
    setResumingCancelled(true);
    try {
      const targetId = await resolveTargetId();
      if (!targetId) throw new Error('Nie udało się ustalić celu do wznowienia.');
      const targetResponse = isKB
        ? await labApi.getKnowledge(productId, targetId)
        : await labApi.getContent(productId, targetId);
      const target = targetResponse.data.data || targetResponse.data;
      const sections = (target.sections || {}) as Record<string, ChoiceSectionState>;
      const resumeFrom = firstIncompletePipelineStep(pipelineFieldsRef.current, sections);
      if (isKB) {
        await labApi.generateKnowledge(productId, {
          kbId: targetId,
          name: kbName || undefined,
          resumeFrom,
        });
      } else {
        await labApi.resumeContent(productId, targetId, { resumeFrom });
      }
      setCancelled(false);
      markPipelineStarted();
    } catch (err) {
      setResumingCancelled(false);
      triggerError(err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string } } }).response?.data?.error || 'Nie udało się wznowić generowania.'
        : err instanceof Error ? err.message : 'Nie udało się wznowić generowania.');
    }
  };

  const handleRestartCancelled = async () => {
    if (!onRestart) return;
    const accepted = await confirm({
      title: 'Rozpocząć generowanie od nowa?',
      message: 'Zapisane checkpointy tej niedokończonej bazy wiedzy zostaną usunięte. Produkt, materiały źródłowe i ukończone zasoby Laboratorium pozostaną bez zmian.',
      confirmLabel: 'Usuń postęp i rozpocznij od nowa',
      cancelLabel: 'Zachowaj postęp',
      danger: true,
    });
    if (!accepted) return;

    setRestartingCancelled(true);
    try {
      await onRestart();
    } catch (err) {
      setRestartingCancelled(false);
      triggerError(generationRequestError(err, 'Nie udało się usunąć przerwanego szkicu bazy wiedzy.'));
    }
  };

  // Completion handler with animation
  const triggerComplete = () => {
    if (completeTriggeredRef.current) return;
    completeTriggeredRef.current = true;

    // Mark all fields as complete
    const allNames = pipelineFieldsRef.current.map(f => f.name);
    setCompletedFields(new Set(allNames));
    setProgress(prev => ({ current: prev.total || allNames.length, total: prev.total || allNames.length }));

    // Show completion state
    setRedirectCountdown(COMPLETE_REDIRECT_SECONDS);
    setShowComplete(true);
  };

  useEffect(() => {
    if (!showComplete) return;

    const interval = window.setInterval(() => {
      setRedirectCountdown(prev => Math.max(0, prev - 1));
    }, 1000);
    const timeout = window.setTimeout(() => onCompleteRef.current(), COMPLETE_REDIRECT_SECONDS * 1000);

    return () => {
      window.clearInterval(interval);
      window.clearTimeout(timeout);
    };
  }, [showComplete]);

  // Handle choice submission
  const handleSubmitChoice = async (automaticValue?: string) => {
    if (!choiceField) return;
    const chosen = automaticValue || selectedChoice;
    const value = chosen === '__custom__' ? customChoice.trim() : chosen;
    if (!value) return;
    const skipNamingProposals = isKB && choiceField === 'naming_strategy_choice' && chosen === '__custom__';
    const selectedCard = chosen === '__custom__'
      ? undefined
      : choiceCards.find(card => card.summary === chosen || choiceOptions[Number(card.id.split(':').at(-1)) - 1] === chosen);
    const choicePayload = chosen === '__custom__'
      ? { selectedOption: value }
      : selectedCard
        ? { cardId: selectedCard.id }
        : { selectedOption: value };

    setSubmittingChoice(true);
    try {
      if (isKB) {
        await labApi.submitKnowledgeChoice(productId, {
          fieldName: choiceField,
          ...choicePayload,
          kbId: activeKbIdRef.current || undefined,
          skipNamingProposals,
        });
      } else if (contentIdRef.current) {
        await labApi.submitContentChoice(productId, contentIdRef.current, {
          fieldName: choiceField,
          ...choicePayload,
        });
      }
      // Mark choice field as completed
      setCompletedFields(prev => new Set([...prev, choiceField]));
      setChoiceField(null);
      setChoiceOptions([]);
      setChoiceCards([]);
      setSelectedChoice(null);
      setCustomChoice('');
    } catch (err) {
      setAutomaticChoices(false);
      autoSubmittedField.current = null;
      console.error('Choice submission failed:', err);
      const message = generationRequestError(err, 'Nie udało się przesłać wyboru.');
      if (isCancelledGenerationMessage(message)) setCancelled(true);
      else triggerError(message);
    } finally {
      setSubmittingChoice(false);
    }
  };

  const submitChoiceRef = useRef(handleSubmitChoice);
  submitChoiceRef.current = handleSubmitChoice;
  useEffect(() => {
    if (!choiceField) { autoSubmittedField.current = null; return; }
    if (!automaticChoices || submittingChoice || regeneratingChoices || choiceOptions.length === 0 || autoSubmittedField.current === choiceField) return;
    const option = automaticGenerationChoice(choiceOptions, choiceCards);
    if (!option) return;
    autoSubmittedField.current = choiceField;
    void submitChoiceRef.current(option);
  }, [automaticChoices, choiceField, choiceOptions, choiceCards, submittingChoice, regeneratingChoices]);

  const hasChoiceSteps = pipelineFields.some(field => field.type === 'choice');
  const automaticButton = !isProductImages && hasChoiceSteps && (
    <Button variant={automaticChoices ? 'primary' : 'secondary'} size="sm" leftIcon={<Sparkles className="h-4 w-4" />} aria-pressed={automaticChoices}
      aria-label={automaticChoices ? 'Wyłącz automatyczny wybór' : 'Włącz automatyczny wybór'}
      title={automaticChoices ? 'Automatyczny wybór włączony. Kliknij, aby samodzielnie wybierać kolejne opcje.' : 'Automatycznie wybiera rekomendowane opcje i kontynuuje generowanie. Możesz wyłączyć w każdej chwili.'}
      onClick={() => setAutomaticChoices(value => !value)}>
      Tryb auto
    </Button>
  );

  const handleRegenerateChoiceOptions = async () => {
    if (!choiceField) return;

    setRegeneratingChoices(true);
    try {
      const payload = {
        fieldName: choiceField,
        selectedOption: '__regenerate_options__',
        regenerateOptions: true,
      };
      const response = isKB
        ? await labApi.submitKnowledgeChoice(productId, {
          ...payload,
          kbId: activeKbIdRef.current || undefined,
        })
        : contentIdRef.current
          ? await labApi.submitContentChoice(productId, contentIdRef.current, payload)
          : null;
      const nextOptions = response?.data?.data?.choiceOptions;
      const nextCards = response?.data?.data?.choiceCards;
      if (Array.isArray(nextOptions) && nextOptions.length > 0) {
        setChoiceOptions(nextOptions);
        setChoiceCards(Array.isArray(nextCards) ? nextCards : []);
        setSelectedChoice(null);
        setCustomChoice('');
      }
    } catch (err) {
      console.error('Choice regeneration failed:', err);
      const message = generationRequestError(err, 'Nie udało się wygenerować innych propozycji.');
      if (isCancelledGenerationMessage(message)) setCancelled(true);
      else triggerError(message);
    } finally {
      setRegeneratingChoices(false);
    }
  };

  // Listen to socket events
  useEffect(() => {
    const handleProgress = (data: {
      labProductId: string;
      pipeline?: string;
      targetId?: string;
      fieldName: string;
      fieldIndex: number;
      totalFields: number;
      status: string;
      content?: string;
      choiceOptions?: string[];
      choiceCards?: ChoiceCard[];
      selectedCardId?: string;
      recommended?: boolean;
    }) => {
      const expectedTargetId = isKB ? activeKbIdRef.current : contentIdRef.current;
      if (!isProgressEventForRun(data, {
        labProductId: productId,
        pipeline: pipelineName,
        targetId: expectedTargetId,
      })) return;
      if (data.status === 'generating') setResumingCancelled(false);

      const repairFieldNames = repairFieldNamesRef.current;
      if (repairFieldNames && !repairFieldNames.has(data.fieldName)) {
        return;
      }
      if (repairFieldNames) {
        const repairIndex = pipelineFieldsRef.current.findIndex(field => field.name === data.fieldName);
        setProgress({
          current: Math.max(1, repairIndex + 1),
          total: pipelineFieldsRef.current.length,
        });
      } else {
        setProgress({ current: data.fieldIndex + 1, total: data.totalFields });
      }
      setCurrentField(data.fieldName);

      // Handle choice fields — pipeline pauses here
      if (data.status === 'awaiting_choice' && data.choiceOptions) {
        setChoiceField(data.fieldName);
        setChoiceOptions(data.choiceOptions);
        setChoiceCards(data.choiceCards || []);
        setSelectedChoice(null);
        setCustomChoice('');
        setRegeneratingChoices(false);
        return;
      }

      if (data.status === 'completed' && data.content) {
        setCompletedFields(prev => new Set([...prev, data.fieldName]));
      }

      // Socket completion can describe one field. Only persisted run state
      // (read below) may start the final countdown.

      if (data.status === 'error') {
        triggerError(data.content || 'Wystąpił błąd podczas generowania.');
      }
      if (data.status === 'cancelled') setCancelled(true);
    };

    const cleanupSocket = onSocketReady((socket) => {
      socket.emit('subscribe:laboratorium');
      socket.off('lab:progress', handleProgress);
      socket.on('lab:progress', handleProgress);
    });

    return () => {
      cleanupSocket?.();
      getSocket()?.off('lab:progress', handleProgress);
      unsubscribeFromLaboratorium();
    };
  }, [productId, pipelineName]);

  // Enhanced polling fallback — updates progress from KB sections or content sections
  useEffect(() => {
    const interval = setInterval(async () => {
      if (completeTriggeredRef.current) return;
      try {
        let sections: Record<string, ChoiceSectionState> = {};
        let isComplete = false;

        if (isProductImages && contentIdRef.current) {
          const [imagesResponse, contentResponse] = await Promise.all([
            labApi.getProductImages(productId, { labContentId: contentIdRef.current }),
            labApi.getContent(productId, contentIdRef.current),
          ]);
          const images = imagesResponse.data.data || [];
          if (!Array.isArray(images)) return;
          const content = contentResponse.data.data || contentResponse.data;
          const persistedError = getProductImagesRunFailure(content);
          if (persistedError) {
            triggerError(persistedError);
            return;
          }
          const runState = getProductImagesRunState(content);

          const fields = pipelineFieldsRef.current;
          const completedImageNames = completedProductImageNames(images, fields);

          if (fields.length > 0 && completedImageNames.length > 0) {
            setCompletedFields(prev => {
              const next = new Set(prev);
              for (const name of completedImageNames) next.add(name);
              return next;
            });
            setProgress(prev => {
              const polledCurrent = completedImageNames.length;
              return polledCurrent > prev.current
                ? { current: polledCurrent, total: prev.total || fields.length }
                : prev;
            });
            const firstIncomplete = fields.find(field => !completedImageNames.includes(field.name));
            if (firstIncomplete) setCurrentField(prev => prev || firstIncomplete.name);
          }

          if (runState?.status === 'completed') {
            triggerComplete();
          }
          return;
        }

        if (isKB) {
          const res = await labApi.getKnowledgeBases(productId);
          const kbs = res.data.data || res.data;
          if (!Array.isArray(kbs) || kbs.length === 0) return;
          const kb = selectGenerationKnowledgeBase(kbs, activeKbIdRef.current);
          if (!kb) return;
          if (kb?.id && !kb.isComplete) activeKbIdRef.current = kb.id;
          const persistedError = getKnowledgePipelineFailure(kb);
          if (persistedError) {
            triggerError(persistedError);
            return;
          }
          isComplete = isTextGenerationComplete(kb);
          sections = (kb.sections || {}) as typeof sections;
        } else if (contentIdRef.current) {
          const res = await labApi.getContent(productId, contentIdRef.current);
          const content = res.data.data || res.data;
          const persistedError = getStaleContentPipelineFailure(content);
          if (persistedError) {
            triggerError(persistedError);
            return;
          }
          isComplete = isTextGenerationComplete(content);
          sections = (content.sections || {}) as typeof sections;
        } else {
          return; // No contentId yet — skip polling
        }

        if (isComplete && repairFieldNamesRef.current && contentIdRef.current) {
          const missingResponse = await labApi.getContentMissingFields(productId, contentIdRef.current);
          const missingNames = new Set(missingResponse.data.data.missingFields.map(field => field.name));
          const repairedNames = pipelineFieldsRef.current
            .map(field => field.name)
            .filter(name => !missingNames.has(name));
          setCompletedFields(new Set(repairedNames));
          setProgress({ current: repairedNames.length, total: pipelineFieldsRef.current.length });
          if (missingNames.size === 0) triggerComplete();
          return;
        }
        if (isComplete) {
          triggerComplete();
          return;
        }

        if (sections.__pipeline_state?.original === 'generating') {
          setResumingCancelled(false);
        }
        if (sections.__pipeline_state?.original === 'cancelled' && !resumingCancelled) {
          setCancelled(true);
          return;
        }

        // Check for awaiting choice in sections
        if (!choiceField) {
          for (const [key, section] of Object.entries(sections)) {
            if (section.awaitingChoice && section.choiceOptions?.length) {
              setChoiceField(key);
              setChoiceOptions(section.choiceOptions);
              setChoiceCards(section.choiceCards || []);
              setSelectedChoice(null);
              setCustomChoice('');
              setRegeneratingChoices(false);
              break;
            }
          }
        }

        // Count completed sections to update progress
        const fields = pipelineFieldsRef.current;
        const fieldNameSet = new Set(fields.map(field => field.name));
        const completedSectionNames = Object.keys(sections).filter(
          key => fieldNameSet.has(key) && sections[key]?.original && !sections[key]?.awaitingChoice
        );
        if (fields.length > 0 && completedSectionNames.length > 0) {
          setCompletedFields(prev => {
            const next = new Set(prev);
            for (const name of completedSectionNames) {
              next.add(name);
            }
            return next;
          });

          setProgress(prev => {
            const polledCurrent = completedSectionNames.length;
            if (polledCurrent > prev.current) {
              return { current: polledCurrent, total: prev.total || fields.length };
            }
            return prev;
          });

          const firstIncomplete = fields.find(f => !completedSectionNames.includes(f.name));
          if (firstIncomplete) {
            setCurrentField(firstIncomplete.name);
          }
        }
      } catch {}
    }, 3000);
    return () => clearInterval(interval);
  }, [productId, choiceField, isKB, isProductImages, resumingCancelled]);

  const totalForCalc = progress.total || pipelineFields.length;
  const pct = totalForCalc > 0
    ? Math.min((progress.current / totalForCalc) * 100, 100)
    : 2;
  const activePipelineItem = pipelineFields.find(field => field.name === (choiceField || currentField));
  const stepMessages = activePipelineItem?.progressMessages?.filter(Boolean);
  const activeMessages = stepMessages && stepMessages.length > 0 ? stepMessages : messages;
  const activeMessage = activeMessages.length > 0
    ? activeMessages[messageIdx % activeMessages.length]
    : '';

  if (generationError) {
    return (
      <LabConfiguratorShell
        title="Nie udało się wygenerować grafik"
        description="Proces został zatrzymany. Ukończone grafiki pozostały zapisane, a szczegóły awarii są widoczne poniżej."
        icon={<ShieldAlert className="h-6 w-6 text-danger" />}
        onClose={onBack}
        footer={onBack ? (
          <button type="button" onClick={onBack} className="btn-primary">
            Wróć do strony produktowej
          </button>
        ) : undefined}
      >
        <div className="rounded-xl border border-danger/25 bg-danger/10 p-5 text-sm leading-relaxed text-text-secondary">
          {generationError}
        </div>
      </LabConfiguratorShell>
    );
  }

  if (cancelled) {
    return (
      <LabConfiguratorShell
        title="Generowanie przerwane"
        description="Ukończone checkpointy pozostały zapisane. Pipeline można wznowić od pierwszego brakującego kroku."
        icon={<Square className="h-6 w-6 text-warning" />}
        onClose={onBack}
        footer={(
          <div className="flex items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <button type="button" onClick={handleResumeCancelled} disabled={resumingCancelled || restartingCancelled} className="btn-primary inline-flex items-center gap-2 disabled:opacity-50">
                {resumingCancelled && <Loader2 className="h-4 w-4 animate-spin" />}
                Wznów generowanie
              </button>
              {isKB && onRestart && (
                <button type="button" onClick={handleRestartCancelled} disabled={resumingCancelled || restartingCancelled} className="btn-danger inline-flex items-center gap-2 disabled:opacity-50">
                  {restartingCancelled ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  Rozpocznij od nowa
                </button>
              )}
            </div>
            {onBack && <button type="button" onClick={onBack} className="btn-secondary">{backLabel}</button>}
          </div>
        )}
      >
        <div className="rounded-xl border border-warning/25 bg-warning/10 p-5 text-sm text-text-secondary">
          Anulowanie dotyczyło wyłącznie tej generacji. Inne procesy produktu działają niezależnie.
        </div>
      </LabConfiguratorShell>
    );
  }

  // Completion state
  if (showComplete) {
    return (
      <LabConfiguratorShell
        title={isKB ? 'Baza wiedzy gotowa!' : isProductImages ? 'Zdjęcia produktowe gotowe!' : 'Treść gotowa!'}
        description={isProductImages ? 'Wszystkie grafiki zostały wygenerowane pomyślnie.' : 'Wszystkie sekcje zostały wygenerowane pomyślnie.'}
        icon={<Sparkles className="h-6 w-6 text-success" />}
        onClose={onComplete}
      >
        <div className="py-6 text-center animate-fade-in">
          <div className="w-full max-w-sm mx-auto bg-white/40 rounded-full h-2.5 overflow-hidden">
            <div className="bg-gradient-to-r from-accent-primary to-accent-info h-2.5 rounded-full w-full transition-all duration-1000" />
          </div>
          <p className="text-sm text-text-primary/30 mt-4 animate-pulse">
            {isKB
              ? `Przechodzę do dashboardu produktu za ${redirectCountdown} s...`
              : isProductImages
                ? `Przechodzę do galerii produktu za ${redirectCountdown} s...`
                : `Przechodzę do podglądu treści za ${redirectCountdown} s...`}
          </p>
        </div>
      </LabConfiguratorShell>
    );
  }

  if (submittingChoice) {
    const activeTitle = pipelineFields.find(field => field.name === choiceField)?.title || 'Wybrany kierunek';
    return (
      <LabConfiguratorShell title="Łączę pomysł z resztą historii" stepLabel={activeTitle}
        description="Rozwijam wybrany wariant, sprawdzam jego logikę i dopasowuję go do wcześniejszych decyzji."
        icon={<Brain className="h-6 w-6" />} headerActions={automaticButton} closeDisabled
        footer={<div className="flex flex-wrap items-center justify-end gap-3"><button type="button" className="inline-flex items-center gap-2 rounded-lg border border-danger/30 px-4 py-2 text-sm font-medium text-danger hover:bg-danger/10 disabled:opacity-50" onClick={handleCancelGeneration} disabled={cancelling}>Przerwij generowanie</button></div>}>
        <div role="status" aria-live="polite" className="flex flex-col items-center gap-6 py-10 text-center">
          <ThinkingCube />
          <p className="text-text-secondary">Układam argumenty w spójną całość...</p>
        </div>
      </LabConfiguratorShell>
    );
  }

  // Choice field UI — pipeline paused, waiting for user selection
  if (choiceField && choiceOptions.length > 0) {
    const choiceTitle = pipelineFields.find(f => f.name === choiceField)?.title || choiceField;
    const isNamingStrategyChoice = isKB && choiceField === 'naming_strategy_choice';
    return (
      <LabConfiguratorShell
        title="Twój wybór jest potrzebny"
        headerActions={automaticButton}
        stepLabel={choiceTitle}
        description="Wybierz kierunek, który rozwinę i połączę z wcześniejszymi ustaleniami."
        icon={<Brain className="h-6 w-6" />}
        onClose={onBack}
        closeDisabled
        scrollRef={choiceScrollRef}
        bodyKey={choiceField}
        footer={(
          <div className="flex flex-wrap items-center justify-between gap-3">
            <button
              onClick={() => { void handleSubmitChoice(); }}
              disabled={submittingChoice || regeneratingChoices || (!selectedChoice || (selectedChoice === '__custom__' && !customChoice.trim()))}
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-accent-primary bg-accent-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {submittingChoice && <Loader2 className="w-4 h-4 animate-spin" />}
              {submittingChoice
                ? 'Rozbudowuję wybrany wariant...'
                : isNamingStrategyChoice && selectedChoice === '__custom__'
                  ? 'Zatwierdź nazwę i pomiń propozycje'
                  : 'Zatwierdź wybór'}
            </button>
            <div className="ml-auto flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={handleCancelGeneration}
              disabled={cancelling}
              className="inline-flex items-center gap-2 rounded-lg border border-danger/30 px-4 py-2 text-sm font-medium text-danger hover:bg-danger/10 disabled:opacity-50"
            >
              {cancelling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Square className="h-4 w-4" />}
              Przerwij generowanie
            </button>
            </div>
          </div>
        )}
      >
          {/* Progress bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-text-primary/70 font-medium">
                Generowanie sekcji {progress.current}/{totalForCalc || '...'}
              </span>
              <span className="text-xs text-text-primary/40">{Math.round(pct)}%</span>
            </div>
            <div className="w-full bg-white/40 rounded-full h-2.5 overflow-hidden backdrop-blur-sm">
              <div
                className="bg-gradient-to-r from-accent-primary to-accent-info h-2.5 rounded-full transition-all duration-700 shadow-[0_0_8px_rgba(74,158,255,0.25)]"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>

          {/* Choice options */}
          <div className="space-y-2 lab-board-slide">
              {choiceOptions.map((option, idx) => {
                const card = choiceCards[idx];
                if (!card) {
                  return (
                    <button
                      key={idx}
                      onClick={() => { setSelectedChoice(option); setCustomChoice(''); }}
                      className={`w-full text-left p-3.5 rounded-xl border transition-all duration-200 text-sm ${
                        selectedChoice === option
                          ? 'border-accent-primary/60 bg-accent-primary/10 text-text-primary'
                          : 'border-border bg-surface-hover/50 text-text-primary/70 hover:border-border-strong hover:bg-surface-raised'
                      }`}
                    >
                      <span className="text-text-primary/30 mr-2 font-mono text-xs">{idx + 1}.</span>
                      {cleanChoiceText(option)}
                    </button>
                  );
                }
                const selected = selectedChoice === option;
                return (
                  <div
                    key={card.id}
                    role="radio"
                    aria-checked={selected}
                    tabIndex={0}
                    onClick={() => { setSelectedChoice(option); setCustomChoice(''); }}
                    onKeyDown={event => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setSelectedChoice(option);
                        setCustomChoice('');
                      }
                    }}
                    className={`w-full cursor-pointer rounded-xl border p-4 text-left transition-all ${selected
                      ? 'border-accent-primary/60 bg-accent-primary/10'
                      : 'border-border bg-surface-hover/50 hover:border-border-strong hover:bg-surface-raised'}`}
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-xs text-text-muted">{idx + 1}.</span>
                      <h3 className="text-sm font-semibold text-text-primary">{card.title}</h3>
                      {card.recommended && (
                        <span className="rounded-full bg-success/10 px-2 py-0.5 text-[11px] font-medium text-success">
                          Rekomendowana
                        </span>
                      )}
                      {card.evidenceStatus !== 'missing' && (
                        <span className="ml-auto rounded-full border border-border px-2 py-0.5 text-[11px] text-text-muted">
                          {EVIDENCE_STATUS_LABELS[card.evidenceStatus]}
                        </span>
                      )}
                    </div>
                    <p className="mt-2 text-sm leading-relaxed text-text-secondary">{card.summary.length === 360 ? cleanChoiceText(option) : card.summary}</p>
                  </div>
                );
              })}

              {/* Custom option */}
              <button
                onClick={() => { setSelectedChoice('__custom__'); }}
                className={`w-full text-left p-3.5 rounded-xl border transition-all duration-200 text-sm ${
                  selectedChoice === '__custom__'
                    ? 'border-accent-primary/60 bg-accent-primary/10 text-text-primary'
                    : 'border-border bg-surface-hover/50 text-text-primary/70 hover:border-border-strong hover:bg-white/8'
                }`}
              >
                <span className="text-text-primary/30 mr-2 font-mono text-xs">+</span>
                {isNamingStrategyChoice ? 'Mam własną finalną nazwę' : 'Wpisz własną propozycję'}
              </button>

              {selectedChoice === '__custom__' && (
                <input
                  type="text"
                  value={customChoice}
                  onChange={e => setCustomChoice(e.target.value)}
                  placeholder={isNamingStrategyChoice ? 'Wpisz finalną nazwę produktu lub marki...' : 'Twoja propozycja...'}
                  autoFocus
                  className="w-full p-3 rounded-xl bg-surface-hover/50 border border-border-strong text-text-primary text-sm placeholder:text-text-primary/30 focus:outline-none focus:border-accent-primary/50"
                />
              )}

              <button
                type="button"
                onClick={handleRegenerateChoiceOptions}
                disabled={submittingChoice || regeneratingChoices}
                className="w-full text-left p-3.5 rounded-xl border border-accent-primary/25 bg-accent-primary/5 text-accent-primary hover:bg-accent-primary/10 hover:border-accent-primary/40 transition-all duration-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {regeneratingChoices ? (
                  <Loader2 className="w-4 h-4 animate-spin shrink-0" />
                ) : (
                  <RefreshCw className="w-4 h-4 shrink-0" />
                )}
                <span>{regeneratingChoices ? 'Generuję inne propozycje...' : 'Wygeneruj inne propozycje'}</span>
              </button>
          </div>
      </LabConfiguratorShell>
    );
  }

  return (
    <LabConfiguratorShell
      title={isKB ? 'Tworzę bazę wiedzy...' : `Generuję ${contentTypeLabel}...`}
      headerActions={automaticButton}
      description={activeMessage}
      stepLabel={pipelineFields.find(field => field.name === currentField)?.title}
      icon={<Brain className="h-6 w-6" />}
      onClose={onBack}
      closeDisabled={!isProductImages}
      footer={isProductImages ? (onBack ? (
        <button type="button" onClick={onBack} className="btn-secondary ml-auto block">
          Zamknij, generuj w tle
        </button>
      ) : undefined) : (
        <div className="flex flex-wrap items-center justify-end gap-3">
        <button
          type="button"
          onClick={handleCancelGeneration}
          disabled={cancelling}
          className="inline-flex items-center gap-2 rounded-lg border border-danger/30 px-4 py-2 text-sm font-medium text-danger hover:bg-danger/10 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {cancelling ? <Loader2 className="h-4 w-4 animate-spin" /> : <Square className="h-4 w-4" />}
          Przerwij generowanie
        </button>
        </div>
      )}
    >
        {/* Warning — prominent, above progress */}
        <div className="bg-warning/10 rounded-xl p-4 border border-warning/20 mb-6">
          <p className="text-sm text-warning leading-relaxed flex items-start gap-2.5 font-medium">
            <ShieldAlert className="w-5 h-5 shrink-0 mt-0.5 text-warning" />
            <span>
              {isKB
                ? 'Poczekaj! Trwa generowanie bazy wiedzy - najważniejszego zasobu Twojego produktu. Nie przerywaj tego procesu.'
                : isProductImages
                  ? 'Galeria produktowa powstaje w tle. Możesz zamknąć to okno i w każdej chwili sprawdzić postęp w sekcji zdjęć.'
                  : `Trwa generowanie treści. Poczekaj na zakończenie procesu - nie zamykaj tej strony.`
              }
            </span>
          </p>
        </div>

        {/* Progress bar */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-text-primary/70 font-medium">
              Generowanie sekcji {progress.current}/{totalForCalc || '...'}
            </span>
            <span className="text-xs text-text-primary/40">{Math.round(pct)}%</span>
          </div>
          <div className="w-full bg-white/40 rounded-full h-2.5 overflow-hidden backdrop-blur-sm">
            <div
              className="bg-gradient-to-r from-accent-primary to-accent-info h-2.5 rounded-full transition-all duration-700 shadow-[0_0_8px_rgba(74,158,255,0.25)]"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        {/* Field checklist */}
        {pipelineFields.length > 0 && (
          <div className="relative min-h-0">
            <div
              ref={checklistScrollRef}
              className="max-h-[42vh] overflow-y-auto px-1 py-4 space-y-1 lab-board-slide lab-scroll-area [mask-image:linear-gradient(to_bottom,transparent,black_16px,black_calc(100%-16px),transparent)]"
            >
              {pipelineFields.map((field) => {
                const isFieldComplete = completedFields.has(field.name);
                const isCurrent = currentField === field.name;
                return (
                  <div
                    key={field.name}
                    ref={(node) => { checklistItemRefs.current[field.name] = node; }}
                    className={`flex items-center gap-2.5 text-sm py-1.5 px-3 rounded-lg transition-all duration-300 ${
                      isCurrent ? 'bg-accent-primary/15 ring-1 ring-inset ring-accent-primary/40' : ''
                    }`}
                  >
                    {isFieldComplete ? (
                      <Check className="w-4 h-4 text-success shrink-0" />
                    ) : isCurrent ? (
                      <Loader2 className="w-4 h-4 text-accent-primary animate-spin shrink-0" />
                    ) : (
                      <div className="w-4 h-4 rounded-full border border-border-strong shrink-0" />
                    )}
                    {field.icon && (
                      <span className="text-base shrink-0">{field.icon}</span>
                    )}
                    <span
                      className={`transition-all duration-300 ${
                        isFieldComplete
                          ? 'text-text-primary/70'
                          : isCurrent
                            ? 'text-text-primary font-medium'
                            : 'text-text-primary/20'
                      }`}
                    >
                      {field.title || field.name}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
    </LabConfiguratorShell>
  );
}
